import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { ArrowLeft } from "lucide-react";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { CocoonMark } from "@/components/CocoonMark";
import { CardCarousel } from "@/components/CardCarousel";
import { ValeursCocoon } from "@/components/ValeursCocoon";
import { FoundersWord } from "@/components/FoundersWord";
import { GardenSection } from "@/components/GardenSection";
import { Eventail } from "@/components/Eventail";
import { Footer } from "@/components/Footer";

export default function Philosophie() {
  const { t } = useTranslation();
  const equiv = t("equiv", { returnObjects: true });

  return (
    <div className="relative min-h-screen bg-background text-foreground overflow-x-clip">
      <div className="grain-overlay" aria-hidden />
      <div className="amber-glow" aria-hidden style={{ top: "-8%", left: "50%", transform: "translateX(-50%)", width: "880px", height: "640px" }} />
      <div className="amber-glow" aria-hidden style={{ top: "58%", left: "-14%", width: "620px", height: "620px", opacity: 0.6 }} />

      <header className="fixed top-0 inset-x-0 z-50 glass">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2 whitespace-nowrap" data-testid="philosophie-brand">
            <img src="/cocoon-logo-glow.png" alt="COCOON MIND" className="h-9 w-auto select-none" style={{ filter: "drop-shadow(0 0 8px hsl(28 96% 56% / 0.30))" }} />
            <span className="font-display text-lg sm:text-2xl tracking-tight"><CocoonMark withMind /></span>
          </Link>
          <div className="flex items-center gap-3 sm:gap-5">
            <LanguageSwitcher className="hidden sm:flex" />
            <Link to="/" data-testid="philosophie-back-btn" className="text-sm text-foreground/60 hover:text-primary transition-colors inline-flex items-center gap-1.5">
              <ArrowLeft size={15} /> {t("landing.philosophie.back")}
            </Link>
            <Link to="/entrer" data-testid="philosophie-enter-btn" className="btn-frost-orange text-sm font-semibold px-4 sm:px-6 py-2 rounded-full whitespace-nowrap">{t("nav.entrer")}</Link>
          </div>
        </div>
      </header>

      <section className="max-w-4xl mx-auto px-6 pt-40 pb-8 text-center">
        <motion.p initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1 }} className="text-primary/80 tracking-[0.3em] uppercase text-xs mb-6" data-testid="philosophie-eyebrow">{t("landing.philosophie.eyebrow")}</motion.p>
        <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.12 }} className="font-display text-gradient-orange text-4xl sm:text-6xl leading-tight pb-[0.06em]" data-testid="philosophie-title">{t("landing.philosophie.title")}</motion.h1>
        <motion.p initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.24 }} className="mt-5 text-base sm:text-lg text-foreground/70 max-w-xl mx-auto leading-relaxed">{t("landing.philosophie.intro")}</motion.p>
      </section>

      <ValeursCocoon />

      <section className="max-w-6xl mx-auto px-6 py-20">
        <p className="text-primary/80 tracking-[0.25em] uppercase text-xs mb-10 text-center">{t("landing.equivEyebrow")}</p>
        <div className="max-w-4xl mx-auto">
          <CardCarousel
            testId="equiv-carousel"
            interval={10000}
            slides={equiv.map((e, i) => (
              <article className="glass frame-pulse rounded-3xl p-8 md:p-12 min-h-[280px] flex flex-col justify-center" data-testid={`equiv-${i}`}>
                <span className="text-foreground/45 line-through font-display text-2xl md:text-3xl">{e.from}</span>
                <h3 className="font-display italic text-3xl md:text-5xl text-primary mt-2 mb-4">{e.to}</h3>
                <p className="text-foreground/65 leading-[1.9] text-base md:text-lg">{e.desc}</p>
              </article>
            ))}
          />
        </div>
      </section>

      <Eventail />

      <FoundersWord />

      <GardenSection />

      <section className="max-w-3xl mx-auto px-6 py-20 text-center">
        <Link to="/" data-testid="philosophie-home-cta" className="btn-frost-orange inline-flex items-center gap-3 px-8 py-4 rounded-full font-semibold text-base">
          <ArrowLeft size={18} /> {t("landing.philosophie.back")}
        </Link>
      </section>

      <Footer />
    </div>
  );
}
